/**
 * TanStack Query hook for fetching Reporter comparison data.
 *
 * Reads from `a2z-reader-derived` via the new SSoT endpoint
 * `/api/reader/cohort/<cohort>/study/<uid>/comparison`. The previous
 * `/api/reporter/study/<uid>` endpoint backed by `reporter-comparisons`
 * is being phased out (see demo-backend/src/reader_endpoints.py).
 *
 * cohort_id is supplied by the worklist row when available. Deriving it from
 * `<study_uid>::<job_id>` remains a compatibility fallback for older callers,
 * but is not authoritative: image inference and comparison review can belong
 * to different cohorts.
 *
 * Returns null (not error) when no comparison data exists for a study.
 */

import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";

// ============================================================================
// Types — match Reporter output schema (PRD-01 §4.4)
// ============================================================================

export interface ComparisonClassification {
  high_sensitivity: "agreement" | "ai_only" | "report_only" | "neither";
  sensitivity_biased: "agreement" | "ai_only" | "report_only" | "neither";
  balanced: "agreement" | "ai_only" | "report_only" | "neither";
  high_specificity: "agreement" | "ai_only" | "report_only" | "neither";
}

export interface ComparisonAI {
  // DDB stores Decimal, surfaced as string on the wire — coerced in fetchComparison.
  probability: number;
  operating_point: {
    high_sensitivity: boolean;
    sensitivity_biased: boolean;
    balanced: boolean;
    high_specificity: boolean;
  };
  top_slices: string[];
  attributes: Record<string, unknown>;
  recommended_threshold?: string;
}

export interface ComparisonReport {
  mentioned: boolean;
  excerpt: string | null;
  mapped_finding: string | null;
}

export interface ComparisonEntry {
  finding: string;
  category: string | null;
  classification: ComparisonClassification;
  ai: ComparisonAI;
  report: ComparisonReport;
}

export interface ReporterComparisonResponse {
  study_uid: string;
  // job_id from the new endpoint; the old endpoint returned run_id —
  // keeping both optional so downstream consumers don't break.
  job_id?: string;
  run_id?: string;
  model?: string;
  comparison_matrix: ComparisonEntry[];
  report_unmapped: unknown[];
  original_report_text: string;
  successful_inference: boolean;
  successful_comparison: boolean;
}

// ============================================================================
// Query Keys
// ============================================================================

export const reporterKeys = {
  all: ["reporter"] as const,
  comparison: (studyUid: string, cohortId: string) =>
    [...reporterKeys.all, "comparison", cohortId, studyUid] as const,
};

// ============================================================================
// Cohort extraction
// ============================================================================

/** job_id shape: `<cohort>-<study_uid>`. Returns null when the suffix doesn't
 * match (e.g. drafter job_ids, or a malformed pair). */
export function parseCohortIdFromJobId(jobId: string, studyUid: string): string | null {
  if (!jobId || !studyUid) return null;
  const suffix = `-${studyUid}`;
  if (!jobId.endsWith(suffix)) return null;
  const cohort = jobId.slice(0, -suffix.length);
  return cohort || null;
}

/** Prefer the cohort attached to the reader row; fall back to legacy job parsing. */
export function resolveReporterCohortId(
  studyUid: string,
  jobId: string,
  readerCohortId?: string | null,
): string | null {
  const explicit = readerCohortId?.trim();
  return explicit || parseCohortIdFromJobId(jobId, studyUid);
}

// ============================================================================
// API Fetch
// ============================================================================

async function fetchComparison(
  cohortId: string,
  studyUid: string,
): Promise<ReporterComparisonResponse | null> {
  const baseUrl = getApiBaseUrl();
  const encodedCohort = encodeURIComponent(cohortId);
  const encodedUid = encodeURIComponent(studyUid);

  try {
    const response = await axios.get<ReporterComparisonResponse>(
      `${baseUrl}/api/reader/cohort/${encodedCohort}/study/${encodedUid}/comparison`,
      { withCredentials: true },
    );
    const data = response.data;
    // probability arrives as a string (DDB Decimal); coerce once at the boundary.
    for (const entry of data.comparison_matrix) {
      entry.ai.probability = Number(entry.ai.probability);
    }
    return data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

// ============================================================================
// Hook
// ============================================================================

export function useReporterComparison(
  studyUid: string | null,
  jobId: string | null,
  readerCohortId?: string | null,
) {
  const cohortId =
    studyUid && jobId ? resolveReporterCohortId(studyUid, jobId, readerCohortId) : null;
  return useQuery({
    queryKey: reporterKeys.comparison(studyUid ?? "", cohortId ?? ""),
    queryFn: () => fetchComparison(cohortId!, studyUid!),
    enabled: !!studyUid && !!cohortId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: (failureCount, error) => {
      // Don't retry 404s (expected for studies without comparison data)
      if (axios.isAxiosError(error) && error.response?.status === 404) return false;
      return failureCount < 1;
    },
  });
}
